import { useState } from 'react';
import { PopoverShell } from '@/components/PopoverShell';
import { useAppSelector } from '@/store/hooks';
import { usePreviewMode } from '@/contexts/PreviewModeContext';
import { useI18nText } from '@/lib/utils/i18nText';
import { formatLocalDateTime, relativeDate, updateFreshnessTier, type UpdateFreshnessTier } from '@/lib/utils/timezone';

const FRESHNESS_LEGEND: { tier: UpdateFreshnessTier; label: [string, string] }[] = [
  { tier: 'recent', label: ['Within 3 days', '3일 이내'] },
  { tier: 'stale', label: ['Within 14 days', '14일 이내'] },
  { tier: 'old', label: ['Over 14 days', '14일 초과'] },
];

/**
 * "Updated at" freshness indicator — a small gray underlined-link chip showing
 * the relative age ("Updated At 2d ago"); clicking it opens a popover with the
 * absolute date/time. Shared by the dashboard freshness lines (ColumnUpdatedAt)
 * and the panel Channels-tab "Updated At" column so both read identically.
 * Returns null when there is no timestamp. Not gated — callers decide visibility.
 *
 * Popover-ized (rather than showing the absolute date inline below, as before)
 * so a later addition — the next scheduled update — has somewhere to go as a
 * second labeled row without needing another redesign.
 */
export function UpdatedAtLines({ iso }: { iso?: string | null }) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  if (!iso) return null;
  const tier = updateFreshnessTier(iso);
  return (
    <PopoverShell
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button type="button" className="updated-at-trigger">
          <span className={`updated-at-trigger__dot updated-at-trigger__dot--${tier}`} aria-hidden />
          {t('Updated At', '갱신일')}{' '}
          <span className={`updated-at-value updated-at-value--${tier}`}>{relativeDate(iso)}</span>
        </button>
      }
      side="bottom"
      align="start"
      width={200}
    >
      <div className="updated-at-popover-row">
        <span className="updated-at-popover-label">{t('Last updated', '최신 업데이트 일자')}</span>
        <span className="updated-at-popover-value">{formatLocalDateTime(iso)}</span>
      </div>
      {/* Color-coding legend — the relative-time label above (and every "Updated
          At" chip in the table) is tinted per this same tiering, so the meaning
          of the color is discoverable from any one instance's popover. */}
      <div className="updated-at-popover-legend">
        {FRESHNESS_LEGEND.map(({ tier: legendTier, label }) => (
          <span key={legendTier} className="updated-at-popover-legend__row">
            <span className={`updated-at-popover-legend__dot updated-at-popover-legend__dot--${legendTier}`} />
            {t(...label)}
          </span>
        ))}
      </div>
    </PopoverShell>
  );
}

/**
 * Muted "last updated" freshness line pinned at the bottom of a data column's cell
 * so an admin can gauge how fresh THAT column's data is (channel stats crawl).
 *
 * Admin-only: hidden in advertiser-preview and guest-token sessions (mirrors the
 * `role === 'admin' && !isPreview` gate used by ChannelNameQuickButtons / V1CompareButton),
 * so this internal freshness metadata never leaks into an advertiser-facing view.
 */
export function ColumnUpdatedAt({ iso }: { iso?: string | null }) {
  const role = useAppSelector((s) => s.envs.currentUser?.role);
  const isPreview = usePreviewMode();
  if (role !== 'admin' || isPreview) return null;
  if (!iso) return null;
  return (
    <div className="column-updated-at">
      <UpdatedAtLines iso={iso} />
    </div>
  );
}
